import { type CvssScore } from './cvss-score';
import { type CvssVectorParser } from './cvss-vector-parser';
import { Cvss2VectorParser } from './v2/cvss2-vector-parser';
import { Cvss3VectorParser } from './v3/cvss3-vector-parser';
import { Cvss4VectorParser } from './v4/cvss4-vector-parser';

// TODO: Rework whole lib and CVSS Context
/**
 * Implements a service offering CVSS vector parsing.
 *
 * @remarks
 * Consumers should be aware that {@link parse} will raise an exception if an invalid CVSS vector string is passed.
 * This includes strings containing incorrect keys/values and those that are missing required entries.
 *
 * @public
 */
export class MultiCvssVectorParser implements CvssVectorParser {
    /**
     * {@inheritdoc}
     */
    public parse(vector: string): CvssScore {
        // Try all CVSS parsers
        const parserChain: CvssVectorParser[] = [
            new Cvss2VectorParser(),
            new Cvss3VectorParser(),
            new Cvss4VectorParser(),
        ];

        let lastError: Error | null = null;

        for (const parser of parserChain) {
            try {
                return parser.parse(vector);
            } catch (error) {
                if (error instanceof Error) {
                    lastError = error;
                }
            }
        }

        if (lastError !== null) {
            throw lastError;
        }

        throw new Error('Failed to parse vector: Unhandled parsing case.');
    }

    /**
     * Parses a CVSS vector string and converts it to a CVSS score.
     * @remarks
     * safeParse won't throw an error when validation fails
     * This method returns an object containing either the successfully parsed data or
     * an Error instance containing information about the last occured problem in the parsing.
     *
     * @param vector - the vector string to parse
     */
    public safeParse(
        vector: string,
    ): { success: true; score: CvssScore } | { success: false; error: Error } {
        try {
            const score = this.parse(vector);
            return {
                success: true,
                score,
            };
        } catch (error) {
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error
                        : new Error(
                              'Failed to parse vector: Unhandled parsing case.',
                          ),
            };
        }
    }

    /*
     * {@inheritdoc}
     */
    public reorderVector(vector: string): string {
        // Try all CVSS parsers
        const parserChain: CvssVectorParser[] = [
            new Cvss2VectorParser(),
            new Cvss3VectorParser(),
            new Cvss4VectorParser(),
        ];

        let lastError: Error | null = null;

        for (const parser of parserChain) {
            try {
                return parser.reorderVector(vector);
            } catch (error) {
                if (error instanceof Error) {
                    lastError = error;
                }
            }
        }

        if (lastError !== null) {
            throw lastError;
        }

        throw new Error('Failed to reorder vector: Unhandled parsing case.');
    }
}
